import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, ExternalLink, IndianRupee, Link as LinkIcon, Image, Loader2, ShoppingBag, Folder, FolderPlus, X, CreditCard, Copy, Check } from 'lucide-react';
import BottomNavigation from '../components/BottomNavigation';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const EditWishlistPage = () => {
  const { wishlistId } = useParams();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showUpiModal, setShowUpiModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [upiId, setUpiId] = useState('');
  const [savingUpi, setSavingUpi] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    link: '',
    image_url: '',
    description: '',
    category_id: null
  });

  useEffect(() => {
    fetchWishlistDetails();
    fetchCategories();
  }, [wishlistId]);

  const fetchWishlistDetails = async () => {
    try {
      const response = await axios.get(`${API}/wishlists/${wishlistId}`, { withCredentials: true });
      setWishlist(response.data.wishlist);
      setItems(response.data.items || []);
      setUpiId(response.data.wishlist?.upi_id || '');
      if (!response.data.is_owner) {
        toast.error('Access denied');
        navigate('/wishlist');
      }
    } catch (error) {
      toast.error('Failed to load wishlist');
      navigate('/wishlist/my-wishlists');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/wishlist/categories`, { withCredentials: true });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleScrapeUrl = async () => {
    const url = formData.link.trim();
    if (!url) {
      toast.error('Please enter a URL first');
      return;
    }

    setScraping(true);
    try {
      const response = await axios.post(`${API}/scrape-product`, { url }, { withCredentials: true });
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setFormData(prev => ({
          ...prev,
          name: data.title || prev.name,
          image_url: data.image_url || prev.image_url,
          price: data.price || prev.price
        }));
        toast.success('Product info fetched!');
      } else {
        toast.info(response.data.error || 'Could not fetch product info');
      }
    } catch (error) {
      toast.error('Failed to fetch product info');
    } finally {
      setScraping(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Item name is required');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        category_id: formData.category_id || null,
        price: formData.price ? parseFloat(formData.price) : null,
        link: formData.link || null,
        image_url: formData.image_url || null,
        description: formData.description || null
      };

      await axios.post(`${API}/wishlists/${wishlistId}/items`, payload, { withCredentials: true });
      toast.success('Item added!');
      setShowAddModal(false);
      resetForm();
      fetchWishlistDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Item name is required');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        category_id: formData.category_id || null,
        price: formData.price ? parseFloat(formData.price) : null,
        link: formData.link || null,
        image_url: formData.image_url || null,
        description: formData.description || null
      };

      await axios.put(`${API}/wishlists/${wishlistId}/items/${editingItem.id}`, payload, { withCredentials: true });
      toast.success('Item updated!');
      setEditingItem(null);
      resetForm();
      fetchWishlistDetails();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;

    try {
      await axios.delete(`${API}/wishlists/${wishlistId}/items/${itemId}`, { withCredentials: true });
      toast.success('Item deleted');
      fetchWishlistDetails();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Enter category name');
      return;
    }

    try {
      await axios.post(`${API}/wishlist/categories`, { name: newCategoryName }, { withCredentials: true });
      toast.success('Category created');
      setNewCategoryName('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? Items will be uncategorized.')) return;

    try {
      await axios.delete(`${API}/wishlist/categories/${categoryId}`, { withCredentials: true });
      toast.success('Category deleted');
      fetchCategories();
      fetchWishlistDetails();
      if (selectedCategory === categoryId) setSelectedCategory('all');
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const openEditModal = (item) => {
    setFormData({
      name: item.name || '',
      category_id: item.category_id || null,
      price: item.price || '',
      link: item.link || '',
      image_url: item.image_url || '',
      description: item.description || ''
    });
    setEditingItem(item);
  };

  const resetForm = () => {
    setFormData({ name: '', price: '', link: '', image_url: '', description: '', category_id: null });
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingItem(null);
    resetForm();
  };

  const handleSaveUpi = async () => {
    setSavingUpi(true);
    try {
      await axios.put(
        `${API}/wishlists/${wishlistId}/upi`,
        { upi_id: upiId.trim() },
        { withCredentials: true }
      );
      toast.success('UPI ID saved successfully');
      setShowUpiModal(false);
      fetchWishlistDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save UPI ID');
    } finally {
      setSavingUpi(false);
    }
  };

  const filteredItems = selectedCategory === 'all'
    ? items
    : selectedCategory === 'uncategorized'
    ? items.filter(item => !item.category_id)
    : items.filter(item => item.category_id === selectedCategory);

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : 'Uncategorized';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 flex items-center justify-center">
        <Loader2 className="text-white animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold p-4 shadow-xl">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/wishlist/my-wishlists')} className="text-white">
            <ArrowLeft size={22} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-white truncate">{wishlist?.name}</h1>
            <p className="text-xs text-gray-200">{items.length} items</p>
          </div>
          <button
            onClick={() => setShowUpiModal(true)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1 ${wishlist?.upi_id ? 'bg-green-500 text-white' : 'bg-yellow-500 text-black'}`}
          >
            <CreditCard size={16} /> {wishlist?.upi_id ? 'UPI ✓' : 'Add UPI'}
          </button>
          <button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="bg-white text-contribo-navy px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1"
          >
            <Plus size={16} /> Add
          </button>
        </div>

        {/* UPI Info Banner */}
        {wishlist?.upi_id && (
          <div className="mt-3 bg-green-500/20 border border-green-400/50 rounded-lg p-2 flex items-center gap-2">
            <CreditCard size={16} className="text-green-300" />
            <span className="text-green-100 text-xs">Contributors will pay to: <strong className="text-white">{wishlist.upi_id}</strong></span>
          </div>
        )}

        {/* Category Tabs */}
        <div className="flex gap-2 mt-3 flex-wrap">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${selectedCategory === 'all' ? 'bg-white text-contribo-navy' : 'bg-contribo-navy text-white border border-white/50'}`}
          >All</button>
          <button
            onClick={() => setSelectedCategory('uncategorized')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${selectedCategory === 'uncategorized' ? 'bg-white text-contribo-navy' : 'bg-contribo-navy text-white border border-white/50'}`}
          >Uncategorized</button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition flex items-center gap-1 ${selectedCategory === cat.id ? 'bg-white text-contribo-navy' : 'bg-contribo-navy text-white border border-white/50'}`}
            >
              <Folder size={12} /> {cat.name}
            </button>
          ))}
          <button
            onClick={() => setShowCategoryModal(true)}
            className="px-2 py-1 rounded-lg text-xs font-semibold bg-purple-600 text-white flex items-center gap-1"
          >
            <FolderPlus size={12} />
          </button>
        </div>
      </div>

      {/* Items */}
      <div className="p-4">
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="mx-auto text-gray-500 mb-3" size={48} />
            <p className="text-white font-semibold">No items yet</p>
            <p className="text-gray-400 text-sm mt-1">Add items to your wishlist</p>
            <button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="mt-4 bg-contribo-gold text-contribo-navy px-4 py-2 rounded-lg font-semibold text-sm"
            >Add First Item</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {filteredItems.map(item => (
              <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="flex">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="w-24 h-24 object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-24 h-24 bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <ShoppingBag className="text-gray-400" size={24} />
                    </div>
                  )}
                  <div className="p-3 flex-1 min-w-0">
                    {item.category_id && (
                      <span className="text-xs text-purple-600 font-medium flex items-center gap-1 mb-1">
                        <Folder size={10} /> {getCategoryName(item.category_id)}
                      </span>
                    )}
                    <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">{item.name}</h3>
                    {item.price && (
                      <p className="text-contribo-gold font-bold text-sm mt-1">₹{item.price.toLocaleString()}</p>
                    )}
                    {item.funded_amount > 0 && item.price && (
                      <div className="mt-1">
                        <div className="bg-gray-200 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="bg-green-500 h-full"
                            style={{ width: `${Math.min((item.funded_amount / item.price) * 100, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          ₹{item.funded_amount.toLocaleString()} / ₹{item.price.toLocaleString()} funded
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex border-t border-gray-100">
                  {item.link && (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 py-2 text-blue-600 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-blue-50"
                    >
                      <ExternalLink size={12} /> View
                    </a>
                  )}
                  <button
                    onClick={() => openEditModal(item)}
                    className="flex-1 py-2 text-gray-600 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-gray-50 border-l border-gray-100"
                  >
                    <Edit2 size={12} /> Edit
                  </button>
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="flex-1 py-2 text-red-500 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-red-50 border-l border-gray-100"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingItem) && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <div className="p-4">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                {editingItem ? 'Edit Item' : 'Add Item'}
              </h2>

              <form onSubmit={editingItem ? handleUpdateItem : handleAddItem} className="space-y-3">
                {/* URL with Scrape */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Product URL</label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={formData.link}
                      onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                      placeholder="https://amazon.in/..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleScrapeUrl}
                      disabled={scraping || !formData.link}
                      className="px-3 py-2 bg-blue-500 text-white rounded-lg text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
                    >
                      {scraping ? <Loader2 size={14} className="animate-spin" /> : <LinkIcon size={14} />}
                      Fetch
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Paste URL and click Fetch to auto-fill details</p>
                </div>

                {/* Category */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Category</label>
                  <select
                    value={formData.category_id || ''}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Uncategorized</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Item Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., iPhone 15 Pro"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    required
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Price (₹)</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="e.g., 129900"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    min="0"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Notes about this item..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    rows={2}
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-600"
                  >Cancel</button>
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-contribo-navy text-white rounded-lg text-sm font-semibold"
                  >{editingItem ? 'Update' : 'Add'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm max-h-[80vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-gray-800">Manage Categories</h2>
                <button onClick={() => setShowCategoryModal(false)} className="text-gray-500">
                  <X size={20} />
                </button>
              </div>

              {/* Add Category */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="New category name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  onClick={handleAddCategory}
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold"
                >Add</button>
              </div>

              {/* Category List */}
              <div className="space-y-2">
                {categories.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No categories yet</p>
                ) : (
                  categories.map(cat => (
                    <div key={cat.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <span className="flex items-center gap-2 text-gray-800 text-sm">
                        <Folder size={16} className="text-purple-600" /> {cat.name}
                      </span>
                      <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <button
                onClick={() => setShowCategoryModal(false)}
                className="w-full mt-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold"
              >Close</button>
            </div>
          </div>
        </div>
      )}

      {/* UPI Modal */}
      {showUpiModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm">
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-gray-800">Payment UPI ID</h2>
                <button onClick={() => setShowUpiModal(false)} className="text-gray-500">
                  <X size={20} />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Add your UPI ID so contributors can send payments directly to you when they contribute to your wishlist items.
              </p>

              <div className="mb-4">
                <label className="text-xs font-semibold text-gray-600 block mb-1">UPI ID</label>
                <input
                  type="text"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  placeholder="yourname@upi or yourname@bank"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Example: name@okicici, name@paytm</p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowUpiModal(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-600"
                >Cancel</button>
                <button
                  onClick={handleSaveUpi}
                  disabled={savingUpi}
                  className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
                >
                  {savingUpi ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  {savingUpi ? 'Saving...' : 'Save UPI'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
};

export default EditWishlistPage;
